import React, { useState, useEffect } from 'react';
import TypingAnimation from '../../components/TypingAnimation';
import { WaitlistInitialData } from '../../components/WaitlistModal';
import { WaitlistSection } from '../../components/WaitlistSection';

interface HomeProps {
  onOpenWaitlist?: (tab?: 'customer' | 'vendor' | 'rider', data?: WaitlistInitialData) => void;
  defaultOpenWaitlist?: boolean;
}

const Home: React.FC<HomeProps> = ({ onOpenWaitlist, defaultOpenWaitlist }) => {
  const [fadeInSections, setFadeInSections] = useState<boolean[]>([false, false, false, false, false, false]);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  useEffect(() => {
    if (defaultOpenWaitlist && onOpenWaitlist) {
      onOpenWaitlist('customer');
    }
  }, [defaultOpenWaitlist, onOpenWaitlist]);

  useEffect(() => {
    const timers = fadeInSections.map((_, index) =>
      setTimeout(() => {
        setFadeInSections(prev => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      }, index * 100)
    );
    return () => timers.forEach(timer => clearTimeout(timer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const faqItems = [
    {
      question: "What is MarketMate?",
      answer: "MarketMate is a platform that connects customers with local farmers, enabling fresh produce delivery directly from farm to your table. We also empower farmers to sell their products and riders to earn by delivering orders."
    },
    {
      question: "How do I order fresh produce?",
      answer: "Simply download our app or visit our website, browse available products from local farmers, add items to your cart, and checkout. We'll deliver your fresh produce within 24-48 hours."
    },
    {
      question: "What payment methods are accepted?",
      answer: "We accept all major credit cards, debit cards, mobile money, and bank transfers. Choose the payment method that works best for you at checkout."
    },
    {
      question: "How fast is delivery?",
      answer: "Most orders are delivered within 24-48 hours depending on your location. Express delivery is available in select areas. Track your order in real-time through our app."
    },
    {
      question: "Where do the products come from?",
      answer: "All products come directly from verified local farmers in your area. We ensure quality and freshness by maintaining direct relationships with our farmer partners."
    },
    {
      question: "How do I join as a vendor or rider?",
      answer: "Farmers and riders can join through our platform by completing a simple registration process. Visit the 'Join Us' section to apply and start earning with MarketMate."
    }
  ];

  return (
    <div className="w-full overflow-hidden">
      {/* Hero Section */}
      <section
        id="hero"
        className="relative w-full min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center overflow-hidden transition-opacity duration-1000"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)), url(/images/Homebg.png), url(/images/Homebg1.png)`,
          backgroundSize: 'cover, cover, cover',
          backgroundPosition: 'center, center, center',
          opacity: fadeInSections[0] ? 1 : 0,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/50"></div>

        <div className="relative z-10 mt-24 text-center px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 animate-floatUp">
            <span className="text-white">From </span>
            <span style={{ color: '#2E7D32' }}>Farm</span>
            <span className="text-white"> to You</span>
          </h1>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white mt-4 animate-floatUp" style={{ animationDelay: '0.1s' }}>
            <TypingAnimation
              text="Made Simple."
              speed={50}
              className="text-orange-400"
              cursor={true}
            />
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed animate-floatUp" style={{ animationDelay: '0.2s' }}>
            Discover the joy of fresh—from farm to your table,<br />
            all in one seamless experience.
          </p>
          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-scaleIn" style={{ animationDelay: '0.3s' }}>
            <button
              onClick={() => onOpenWaitlist && onOpenWaitlist('customer')}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 px-8 rounded-full transition-smooth text-base shadow-lg shadow-emerald-600/30 transform hover:scale-105"
            >
              Join Customer Waitlist →
            </button>
            <button
              onClick={() => onOpenWaitlist && onOpenWaitlist('vendor')}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white font-bold py-3.5 px-8 rounded-full transition-smooth text-base shadow-lg shadow-orange-500/30 transform hover:scale-105"
            >
              Become a Merchant Partner →
            </button>
          </div>
        </div>
      </section>

      {/* Section Two */}
      <section
        className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-24 transition-opacity duration-1000"
        style={{
          backgroundImage: 'url(/images/Homesect2bg.png)',
          opacity: fadeInSections[1] ? 1 : 0,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#f2fef4] via-[#f7fff9] to-[#ffffff] opacity-95"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="relative flex justify-center lg:justify-end">
              <div className="w-full max-w-xl rounded-[2rem] shadow-2xl shadow-slate-900/10 overflow-hidden">
                <img src="/images/Homesec2.png" alt="Farm delivery illustration" className="w-full h-auto" />
              </div>
            </div>

            <div className="max-w-2xl">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-orange-500">Fresh from the source</p>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mb-6">
                Fresh from the <span className="text-emerald-700">Farm</span> <br />
                <span className="text-orange-500">Delivered</span> to You
              </h2>
              <p className="text-base sm:text-lg text-slate-700 leading-8 mb-10">
                We're more than just an ordering app—we're a platform that connects you directly with farmers. Browse fresh produce, order with ease, and get it delivered by trusted riders—all in one seamless experience.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="group rounded-[1.75rem] border border-slate-200 bg-white/95 p-5 shadow-xl shadow-slate-300/30 hover:shadow-2xl hover:shadow-orange-200/30 transition-all duration-300 cursor-pointer hover:-translate-y-1 animate-floatUp" style={{ animationDelay: '0.1s' }}>
                  <div className="flex items-start gap-3 mb-3">
                    <svg className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm11 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM5 11l1.5-4.5h11L19 11H5z" /></svg>
                    <p className="text-sm font-semibold text-slate-900">Fast & Reliable Delivery</p>
                  </div>
                  <p className="text-sm text-slate-600 ml-9">Fresh produce delivered right when you need it.</p>
                </div>
                <div className="group rounded-[1.75rem] border border-slate-200 bg-white/95 p-5 shadow-xl shadow-slate-300/30 hover:shadow-2xl hover:shadow-orange-200/30 transition-all duration-300 cursor-pointer hover:-translate-y-1 animate-floatUp" style={{ animationDelay: '0.2s' }}>
                  <div className="flex items-start gap-3 mb-3">
                    <svg className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" /></svg>
                    <p className="text-sm font-semibold text-slate-900">Transparent Sourcing</p>
                  </div>
                  <p className="text-sm text-slate-600 ml-9">Know where every item comes from and who grows it.</p>
                </div>
                <div className="group rounded-[1.75rem] border border-slate-200 bg-white/95 p-5 shadow-xl shadow-slate-300/30 hover:shadow-2xl hover:shadow-orange-200/30 transition-all duration-300 cursor-pointer hover:-translate-y-1 animate-floatUp" style={{ animationDelay: '0.3s' }}>
                  <div className="flex items-start gap-3 mb-3">
                    <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5s-5 2.24-5 5v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" /></svg>
                    <p className="text-sm font-semibold text-slate-900">Secure & Easy Payments</p>
                  </div>
                  <p className="text-sm text-slate-600 ml-9">Safe checkout with flexible payment options.</p>
                </div>
                <div className="group rounded-[1.75rem] border border-slate-200 bg-white/95 p-5 shadow-xl shadow-slate-300/30 hover:shadow-2xl hover:shadow-orange-200/30 transition-all duration-300 cursor-pointer hover:-translate-y-1 animate-floatUp" style={{ animationDelay: '0.4s' }}>
                  <div className="flex items-start gap-3 mb-3">
                    <svg className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24"><path d="M17.92 7.02C17.45 4.18 14.97 2 12 2c-2.97 0-5.45 2.18-5.92 5.02C4.97 7.55 3 9.76 3 12.5 3 16.04 5.96 19 9.5 19h8c3.04 0 5.5-2.46 5.5-5.5 0-2.64-2.05-4.78-4.58-4.98zM14 13h-3v3h-2v-3H6v-2h3V8h2v3h3v2z" /></svg>
                    <p className="text-sm font-semibold text-slate-900">Fresh from Local Farmers</p>
                  </div>
                  <p className="text-sm text-slate-600 ml-9">Support local growers with every order.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Three */}
      <section className="relative w-full py-24 bg-white overflow-hidden transition-opacity duration-1000" style={{ opacity: fadeInSections[2] ? 1 : 0 }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 mb-4 animate-slideInBounce">Sell Your Farm Produce</h2>
            <p className="text-4xl sm:text-5xl md:text-5xl font-bold text-emerald-700 mb-8 animate-slideInBounce" style={{ animationDelay: '0.1s' }}>Without the Stress</p>
            <p className="text-base sm:text-lg text-slate-700 leading-8 max-w-3xl mx-auto animate-floatUp" style={{ animationDelay: '0.2s' }}>
              No need for a physical shop or complex tools. List your produce on our platform, reach real customers, and let us take care of orders, pickup, and delivery – so you can focus on what you do best.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <img src="/images/Homesec3.png" alt="Sell your farm produce without stress" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Section Four */}
      <section
        className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-24 transition-opacity duration-1000"
        style={{ backgroundImage: 'url(/images/Homesec4bg.png)', opacity: fadeInSections[3] ? 1 : 0 }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#e8f5f0] via-[#f0faf7] to-[#f8fcfb] opacity-95"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="relative flex justify-center lg:justify-start">
              <div className="w-full max-w-lg">
                <img src="/images/Homesec4.png" alt="Delivery rider earning freshness" className="w-full h-auto" />
              </div>
            </div>
            <div className="max-w-2xl">
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mb-2 animate-slideInBounce">Deliver <span className="text-emerald-700">Freshness</span>.</h2>
              <p className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8 animate-slideInBounce" style={{ animationDelay: '0.1s' }}><span className="text-orange-500">Earn</span> on Your Terms.</p>
              <p className="text-base sm:text-lg text-slate-700 leading-8 mb-10 animate-floatUp" style={{ animationDelay: '0.2s' }}>
                Join our rider network and help deliver fresh produce from local farmers to customers. Work on your schedule, earn consistently, and be part of a system that keeps communities moving.
              </p>
              <div className="space-y-4">
                {[
                  { icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z', color: 'text-orange-500', label: 'Earn daily, on your schedule', delay: '0.3s' },
                  { icon: 'M11.99 5V1h-1v4c-5.52 0-10 4.48-10 10s4.48 10 10 10 10-4.48 10-10h-2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z', color: 'text-blue-500', label: 'Flexible hours—you choose when to ride', delay: '0.4s' },
                  { icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z', color: 'text-red-500', label: 'Deliver within your local area', delay: '0.5s' },
                  { icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-8-8h16v2H4z', color: 'text-red-600', label: 'Full support from our team', delay: '' },
                ].map((item, i) => (
                  <div key={i} className={`flex items-start gap-4 bg-white/80 rounded-2xl p-5 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-orange-200/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer ${item.delay ? 'animate-floatUp' : ''}`} style={item.delay ? { animationDelay: item.delay } : {}}>
                    <svg className={`w-6 h-6 ${item.color} flex-shrink-0`} fill="currentColor" viewBox="0 0 24 24"><path d={item.icon} /></svg>
                    <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Five */}
      <section className="relative w-full py-24 bg-white overflow-hidden transition-opacity duration-1000" style={{ opacity: fadeInSections[4] ? 1 : 0 }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 mb-8 animate-slideInBounce">Be a Part of Us</h2>
            <p className="text-base sm:text-lg text-slate-700 leading-8 max-w-3xl mx-auto animate-floatUp" style={{ animationDelay: '0.1s' }}>
              Whether you're buying fresh produce, selling from your farm, or delivering orders, MARKETMATE connects you to a seamless system built for everyone.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="w-full max-w-5xl">
              <img src="/images/Homesec5.png" alt="Be a part of us - Customer, Vendor, Rider" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Embedded Public Waitlist Section */}
      <WaitlistSection onOpenWaitlist={onOpenWaitlist} />

      {/* Section Six - FAQ */}
      <section
        className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-24 transition-opacity duration-1000"
        style={{ backgroundImage: 'url(/images/Homesec6bg.png)', opacity: fadeInSections[5] ? 1 : 0 }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-green-600/85 via-green-600/80 to-green-700/85"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-white mb-4 animate-slideInBounce">Frequently Asked Questions</h2>
            <p className="text-base sm:text-lg text-white/90 animate-floatUp" style={{ animationDelay: '0.1s' }}>Everything you need to know about MarketMate</p>
          </div>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className="bg-white/90 rounded-2xl overflow-hidden shadow-lg shadow-slate-900/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-floatUp"
                style={{ animationDelay: `${0.2 + index * 0.1}s` }}
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 sm:px-8 py-5 flex items-center justify-between text-left hover:bg-white/95 transition-colors"
                >
                  <span className="text-base sm:text-lg font-semibold text-slate-900 pr-4">{item.question}</span>
                  <svg className={`w-6 h-6 text-emerald-700 flex-shrink-0 transition-transform ${expandedFAQ === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 sm:px-8 pb-5 border-t border-slate-200 animate-slideInUp">
                    <p className="text-slate-700 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;